/**
 * AXQA-20260618-04 (2026-06-18) — shared hermes-warmup helper.
 *
 * Before this helper existed each call site (PublicChat,
 * HermesPrewarmTrigger, etc.) fired `/api/health/warmup` (anonymous) or
 * `/api/me/hermes-warmup` (authed) every single mount. With SPA-style
 * navigation that fires N times in 30s as the owner clicks through the
 * studio sidebar, while the container DO is already warm. Wasted bytes,
 * polluted logs, no perceived speedup.
 *
 * What this helper actually buys:
 *   - skip the request entirely if a successful warmup landed within the
 *     last 45s (less than CF Containers' typical scale-down window so
 *     we're guaranteed the container is still warm)
 *   - persist the last-warmup timestamp in sessionStorage so it survives
 *     route transitions within the same tab
 *   - one log line per state transition rather than per call site, so a
 *     headless DOM check still sees `[warmup]` markers for verification
 *
 * Returns a Promise that resolves once the ping is settled OR
 * immediately if skipped — callers that want to await warmup before
 * sending a message can do so without branching on the skip case.
 */
const WARMUP_TTL_MS = 45_000;
const STORAGE_KEY = 'hermes_warmup_last_at';

function lastWarmupAt(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function recordWarmup(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // sessionStorage quota / disabled — fall through; next call refires.
  }
}

export async function fireHermesWarmup(
  path: '/api/health/warmup' | '/api/me/hermes-warmup',
): Promise<void> {
  const ageMs = Date.now() - lastWarmupAt();
  if (ageMs < WARMUP_TTL_MS) return;
  try {
    const init: RequestInit = { cache: 'no-store' };
    if (path === '/api/me/hermes-warmup') init.credentials = 'include';
    const r = await fetch(path, init);
    if (r.ok) recordWarmup();
  } catch {
    // Silent — warmup is best-effort.
  }
}
